<template>
  <div class="maintenance-form cols">
    <label v-if="editMode" class="mb-5">
      {{ $t('maintenance.status') }}: <strong>{{ displayStatusLabel }}</strong>
    </label>

    <label for="maintenanceName">{{ $t('maintenance.name') }}</label>
    <input
        id="maintenanceName"
        v-model="name"
        type="text"
        maxlength="255"
        :disabled="readOnlyMode"
    />

    <label for="maintenanceDescription">{{ $t('maintenance.description') }}</label>
    <textarea
        id="maintenanceDescription"
        class="description-field"
        v-model="description"
        maxlength="2000"
        :disabled="readOnlyMode"
    />

    <label for="maintenanceAmount" class="mt-10">{{ $t('maintenance.amount') }}</label>
    <input
        id="maintenanceAmount"
        v-model.number="amount"
        type="number"
        min="0"
        step="0.01"
        :disabled="readOnlyMode"
    />
    <label v-if="completeMode && !readOnlyMode && !isPastTargetDateMode" class="rows align-items-center mt-10">
      <input v-model="reschedule" type="checkbox" class="mr-5" />
      {{ $t('maintenance.reschedule') }}
    </label>

    <div class="cols mt-10">
      <label class="rows align-items-center mb-5">
        <input
            v-model="isOdometerPeriodEnabled"
            type="checkbox"
            class="mr-5"
            :disabled="readOnlyMode || (isPastTargetDateMode && !isOdometerPeriodEnabled)"
        />
        {{ $t('maintenance.odometer-period') }}
      </label>
      <label class="mb-5">{{ $t('maintenance.current-odometer', {odometer: displayCurrentOdometer}) }}</label>
      <div class="rows odometer-fields">
        <div class="half cols pr-5">
          <label for="odometerPeriod">{{ $t('maintenance.period') }}</label>
          <input
              id="odometerPeriod"
              v-model.number="odometerPeriod"
              type="number"
              min="0"
              step="0.01"
              :disabled="!isOdometerPeriodEnabled || readOnlyMode"
              @input="onOdometerPeriodInput"
          />
        </div>
        <div class="half cols pl-5">
          <label for="targetOdometer">{{ $t('maintenance.odometer') }}</label>
          <input
              id="targetOdometer"
              v-model.number="targetOdometer"
              type="number"
              step="0.01"
              :disabled="isTargetOdometerDisabled"
              @input="onTargetOdometerInput"
          />
        </div>
      </div>
    </div>

    <div class="cols mt-10">
      <label class="rows align-items-center mb-5">
        <input v-model="isDatePeriodEnabled" type="checkbox" class="mr-5" :disabled="readOnlyMode" />
        {{ $t('generic.date') }}
      </label>
      <Datepicker
          id="targetDate"
          v-model="targetDate"
          @update:model-value="targetDateChanged"
          :format="$t('settings.generic.date-format')"
          :enable-time-picker="false"
          :clearable="true"
          auto-apply
          :disabled="!isDatePeriodEnabled || readOnlyMode"
      />
    </div>

    <div class="rows justify-end mt-5">
      <button type="button" :class="{'mr-5': !readOnlyMode}" @click="$emit('cancel')">
        {{ readOnlyMode ? $t('generic.close') : $t('generic.cancel') }}
      </button>
      <button v-if="!readOnlyMode" type="button" :disabled="!canSave" @click="save">
        {{ $t('generic.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import Datepicker from "@vuepic/vue-datepicker";
import {actions} from "@/assets/js/vue/vuex/actions";
import {dataManipulator} from "@/assets/js/data-manipulation";

export default {
  name: 'CreateMaintenanceForm',
  components: {Datepicker},
  emits: ['save', 'cancel'],
  props: {
    initialData: {
      type: Object,
      default: null,
    },
    vehicle: {
      type: Object,
      default: null,
    },
    completeMode: {
      type: Boolean,
      default: false,
    },
    readOnlyMode: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    const initialName = this.initialData?.name ?? '';
    const initialDescription = this.initialData?.description ?? '';
    const initialAmount = this.initialData?.amount ?? null;
    const initialCurrentOdometer = this.roundToTwoDecimals(this.initialData?.currentOdometer);
    let initialOdometerPeriod = this.roundToTwoDecimals(this.initialData?.odometerPeriod);
    let initialTargetOdometer = this.roundToTwoDecimals(this.initialData?.targetOdometer);
    const initialTargetDate = this.initialData?.targetDate ? new Date(this.initialData?.targetDate) : null;
    const initialCompletionDate = this.initialData?.completionDate ? new Date(this.initialData?.completionDate) : null;

    return {
      name: initialName,
      description: initialDescription,
      amount: initialAmount,
      reschedule: this.initialData?.reschedule ?? false,
      currentOdometer: initialCurrentOdometer,
      isOdometerPeriodEnabled: this.initialData?.odometerPeriodConfigured,
      odometerPeriod: initialOdometerPeriod,
      targetOdometer: initialTargetOdometer,
      isDatePeriodEnabled: this.initialData?.targetDateConfigured,
      targetDate: initialTargetDate,
      completionDate: initialCompletionDate,
    };
  },
  computed: {
    hasSelectedPeriod() {
      if (this.completeMode) {
        return true;
      }

      return this.isOdometerPeriodEnabled || this.isDatePeriodEnabled;
    },
    hasRequiredPeriodValues() {
      if (this.completeMode) {
        return true;
      }

      const odometerPeriod = this.roundToTwoDecimals(this.odometerPeriod);
      const targetOdometer = this.roundToTwoDecimals(this.targetOdometer);
      const hasOdometerPeriodValue = this.isPastTargetDateMode || !this.isOdometerPeriodEnabled || (
          odometerPeriod !== null &&
          odometerPeriod >= 0 &&
          targetOdometer !== null &&
          (this.currentOdometer === null || targetOdometer >= this.currentOdometer || this.isCompletedMaintenance)
      );
      const hasPastDateTargetOdometerValue = !this.isPastTargetDateMode || (
          targetOdometer !== null
      );
      const hasTargetDateValue = !this.isDatePeriodEnabled || this.isValidDate(this.targetDate);
      return hasOdometerPeriodValue && hasPastDateTargetOdometerValue && hasTargetDateValue;
    },
    canSave() {
      return this.name.trim().length > 0 && this.hasSelectedPeriod && this.hasRequiredPeriodValues;
    },
    isPastTargetDateMode() {
      return this.isValidDate(this.targetDate) && this.targetDate < this.minTargetDate;
    },
    isCompletedMaintenance() {
      return this.isValidDate(this.completionDate);
    },
    isTargetOdometerDisabled() {
      return (!this.isOdometerPeriodEnabled && !this.isPastTargetDateMode) || this.readOnlyMode;
    },
    minTargetDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    displayCurrentOdometer() {
      if (this.currentOdometer === null) {
        return '-';
      }

      return this.currentOdometer.toFixed(2);
    },
    displayStatusLabel() {
      return this.initialData?.statusLabel ?? '-';
    }
  },
  watch: {
    isPastTargetDateMode(isPast) {
      if (!isPast) {
        return;
      }

      if (!this.isOdometerPeriodEnabled) {
        this.odometerPeriod = null;
      }
    },
    isDatePeriodEnabled(isEnabled) {
      if (isEnabled) {
        if (!this.isValidDate(this.targetDate)) {
          this.targetDate = new Date();
        }
        return;
      }

      if (!this.isOdometerPeriodEnabled) {
        this.targetOdometer = null;
      }
      this.targetDate = null;
    }
  },
  methods: {
    async targetDateChanged() {
      if (!this.isPastTargetDateMode || this.isOdometerPeriodEnabled) {
        return;
      }

      if (!this.vehicle?.id || !this.isValidDate(this.targetDate)) {
        return;
      }

      const payload = {
        id: this.vehicle.id,
        dateFrom: dataManipulator.dateToString(this.targetDate),
        dateTo: dataManipulator.dateToString(this.targetDate),
      };
      const result = await this.$store.dispatch(actions.GET_VEHICLE_ODOMETER, payload);
      this.targetOdometer = this.roundToTwoDecimals(result?.[0]?.odometer);
    },
    roundToTwoDecimals(value) {
      if (value === '' || value === null || value === undefined) {
        return null;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return null;
      }

      return Number(numericValue.toFixed(2));
    },
    isValidDate(value) {
      return value instanceof Date && !Number.isNaN(value.getTime());
    },
    toTargetDateString(value) {
      if (!this.isValidDate(value)) {
        return null;
      }

      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    onOdometerPeriodInput() {
      if (!this.isOdometerPeriodEnabled) {
        return;
      }

      let period = this.roundToTwoDecimals(this.odometerPeriod);
      if (period === null) {
        this.targetOdometer = null;
        return;
      }

      if (period < 0) {
        period = 0;
      }

      this.odometerPeriod = this.roundToTwoDecimals(period);

      if (this.currentOdometer !== null) {
        this.targetOdometer = this.roundToTwoDecimals(this.currentOdometer + this.odometerPeriod);
      }
    },
    onTargetOdometerInput() {
      if (this.isPastTargetDateMode && !this.isOdometerPeriodEnabled) {
        const target = this.roundToTwoDecimals(this.targetOdometer);
        this.targetOdometer = this.roundToTwoDecimals(target);
        return;
      }

      if (!this.isOdometerPeriodEnabled) {
        return;
      }

      let target = this.roundToTwoDecimals(this.targetOdometer);
      if (target === null) {
        this.odometerPeriod = null;
        return;
      }

      this.targetOdometer = this.roundToTwoDecimals(target);

      if (this.currentOdometer !== null) {
        if (this.targetOdometer < this.currentOdometer) {
          return;
        }
        this.odometerPeriod = this.roundToTwoDecimals(this.targetOdometer - this.currentOdometer);
      }
    },
    save() {
      if (!this.canSave) {
        return;
      }

      this.$emit('save', {
        name: this.name.trim(),
        description: this.description?.trim() || null,
        amount: this.roundToTwoDecimals(this.amount),
        reschedule: this.completeMode ? Boolean(this.reschedule) : false,
        odometerPeriod: this.isOdometerPeriodEnabled ? this.roundToTwoDecimals(this.odometerPeriod) : null,
        odometerPeriodConfigured: Boolean(this.isOdometerPeriodEnabled),
        targetOdometer: this.isOdometerPeriodEnabled || this.isPastTargetDateMode ? this.roundToTwoDecimals(this.targetOdometer) : null,
        targetDate: this.isDatePeriodEnabled ? this.toTargetDateString(this.targetDate) : null,
        targetDateConfigured: Boolean(this.isDatePeriodEnabled),
      });
    }
  }
}
</script>

<style scoped lang="scss">
.maintenance-form {
  text-align: left;

  .description-field {
    min-height: 120px;
  }

  .odometer-fields {
    width: 100%;
  }
}
</style>
